import { action, computed, makeObservable, observable } from 'mobx'
import { EventEmitter } from '../../util/EventEmitter'
import { ServerIdentities } from '../../types'
import type { MeetingCubit } from '../MeetingCubit'
import { DisposerTracker } from '../../util/DisposerTracker'
import { roomStateUpdatePumpSlideWithActiveSlideChangedAt } from '../../firestore/RoomState'

// How long the client waits for the Secretary to appear before it logs a
// not-seen observation and starts pumping the slide to force a respawn.
export const SECRETARY_INIT_TIMEOUT_MS = 60_000

// How often the group leader pumps the slide while the Secretary is missing.
export const SECRETARY_RECONNECT_INTERVAL_MS = 15_000

export type SecretaryConnectionState =
  | 'initializing'
  | 'connected'
  | 'disconnected'

export type SecretaryConnectionEvents = {
  connected: []
  disconnected: []
  reconnected: []
  timeout: []
}

// generateObservationId returns an id that ties together every telemetry event
// for one browser observation of the Secretary (one room join in one tab). It
// returns a full UUID rather than a truncated one: the id is a join key across
// many controller instances, and 8 hex chars (32 bits) collide at roughly a
// coin flip by ~77k instances, which would merge unrelated observations. It
// falls back to a timestamp-based id where crypto.randomUUID is unavailable.
function generateObservationId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return `obs-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

/**
 * Tracks whether the Secretary (the server-side transcription participant) is
 * present in the LiveKit room and reports the browser's observation of it to
 * telemetry.
 *
 * These are client observations, not a backend health check. Every event
 * carries `secretary_observation_id` scoped to this tab. When the Secretary is
 * seen, the event carries `observed_transcription_run_id` — the backend run id
 * the Secretary published as a LiveKit participant attribute — so the
 * observation can be joined to the exact backend run. A browser that never sees
 * the Secretary has no run id; that absence is itself the mismatch signal. See
 * docs/systems/telemetry/secretary.md.
 */
export class SecretaryConnectionController {
  @observable
  state: SecretaryConnectionState = 'initializing'

  // Stable id for this observation's whole lifecycle (one room join, one tab).
  readonly observationId = generateObservationId()

  // The backend transcription_run_id the Secretary published as a participant
  // attribute, once observed. Undefined until the Secretary is seen with the
  // attribute set.
  private observedRunId: string | undefined

  private disposers = new DisposerTracker()

  private reconnectInterval: ReturnType<typeof setInterval> | null = null
  private initTimeout: ReturnType<typeof setTimeout> | null = null

  // Timing state used to enrich lifecycle events. All are epoch milliseconds
  // except retryCount (a count of slide pumps so far).
  private connectStartedAt = Date.now()
  private connectedAt: number | null = null
  private timedOut = false
  private timedOutAt: number | null = null
  private retryCount = 0
  private initialized = false
  private disposed = false

  events = new EventEmitter<SecretaryConnectionEvents>()

  constructor(private meeting: MeetingCubit) {
    makeObservable(this)
  }

  initialize() {
    if (this.doesNotNeedSecretary) return
    // Only the first initialize of an observation starts the clock. Guard
    // against a repeat call (state stays 'initializing' until the Secretary
    // appears) re-stamping connectStartedAt and firing a spurious
    // observation_started, which would skew elapsed_ms on later events.
    if (this.initialized) return
    this.initialized = true
    this.connectStartedAt = Date.now()
    this.meeting.logEvent('secretary_participant_observation_started', {
      secretary_observation_id: this.observationId,
      deadline_ms: SECRETARY_INIT_TIMEOUT_MS,
    })
    this.startInitTimeout()
  }

  @computed
  get doesNotNeedSecretary(): boolean {
    return this.meeting.isPreview || this.meeting.isDemo
  }

  @computed
  get isSecretaryMissing(): boolean {
    if (this.doesNotNeedSecretary) return false
    return this.state !== 'connected'
  }

  @computed
  get isSecretaryConnected(): boolean {
    if (this.doesNotNeedSecretary) return true
    return this.state === 'connected'
  }

  @computed
  get isSecretaryDisconnected(): boolean {
    if (this.doesNotNeedSecretary) return false
    return this.state === 'disconnected'
  }

  @action
  handleParticipantIdsUpdate(
    participantIds: string[],
    secretaryRunId?: string
  ) {
    if (this.doesNotNeedSecretary) return

    const secretaryPresent = participantIds.includes(ServerIdentities.Secretary)

    // Capture the observed backend run id whenever it is present. It can arrive
    // on the first sighting or on a later attribute update; keep the latest.
    if (secretaryRunId) {
      this.observedRunId = secretaryRunId
    }

    if (secretaryPresent) {
      if (this.state === 'initializing') {
        this.state = 'connected'
        this.stopInitTimeout()
        this.logSeen()
        this.events.emit('connected')
      } else if (this.state === 'disconnected') {
        this.state = 'connected'
        this.logSeenAfterLoss()
        this.stopReconnectInterval()
        this.events.emit('reconnected')
      }
    } else {
      if (this.state === 'connected') {
        this.state = 'disconnected'
        this.logLost()
        this.startReconnectInterval()
        this.events.emit('disconnected')
      }
    }
  }

  // logSeen records the first sighting of the Secretary in this observation.
  private logSeen() {
    const now = Date.now()
    this.connectedAt = now
    const params: Record<string, unknown> = {
      secretary_observation_id: this.observationId,
      elapsed_ms: now - this.connectStartedAt,
      retry_ordinal: this.retryCount,
    }
    if (this.observedRunId) {
      params.observed_transcription_run_id = this.observedRunId
    }
    this.meeting.logEvent('secretary_participant_seen', params)
  }

  // logSeenAfterLoss records the Secretary reappearing after a loss and, when
  // the loss was a timeout, labels it a recovery so the earlier not-seen event
  // can be classed as harmless.
  private logSeenAfterLoss() {
    const now = Date.now()
    this.connectedAt = now
    const params: Record<string, unknown> = {
      secretary_observation_id: this.observationId,
      elapsed_ms: now - this.connectStartedAt,
      retry_ordinal: this.retryCount,
      recovered_from_timeout: this.timedOut,
    }
    if (this.timedOut && this.timedOutAt !== null) {
      params.ms_since_timeout = now - this.timedOutAt
    }
    if (this.observedRunId) {
      params.observed_transcription_run_id = this.observedRunId
    }
    this.meeting.logEvent('secretary_participant_seen_after_loss', params)
    // A later reconnect in this same observation is a plain reconnect, not a
    // timeout recovery, so clear the timeout marker once we have recovered.
    this.timedOut = false
    this.timedOutAt = null
  }

  private logLost() {
    const now = Date.now()
    const params: Record<string, unknown> = {
      secretary_observation_id: this.observationId,
    }
    if (this.connectedAt !== null) {
      params.connected_duration_ms = now - this.connectedAt
    }
    // Land the run id on the lost event when known. The id can arrive after the
    // sighting (via a later attribute update), so seen/seen_after_loss may not
    // have carried it; without this, a seen -> id-arrives -> lost -> dispose
    // sequence would emit the id on no event, leaving the observation unjoinable.
    if (this.observedRunId) {
      params.observed_transcription_run_id = this.observedRunId
    }
    this.meeting.logEvent('secretary_participant_lost', params)
    // Drop the observed run id: the next Secretary in this room may be a
    // different backend run, and reusing the old id would wrongly join two
    // runs. The replacement's own attribute repopulates it when it appears.
    this.observedRunId = undefined
    // Restart the wait clock so elapsed_ms on the next sighting measures the
    // outage (time the Secretary was gone), not the time since room join. A
    // plain reconnect after a long healthy session would otherwise report an
    // elapsed_ms of minutes and read like a very slow connect.
    this.connectStartedAt = now
  }

  private startInitTimeout() {
    if (this.doesNotNeedSecretary) return
    if (this.state !== 'initializing') return
    if (this.initTimeout) return

    this.initTimeout = setTimeout(() => {
      if (this.state !== 'initializing') return
      this.handleInitTimeout()
    }, SECRETARY_INIT_TIMEOUT_MS)
  }

  @action
  private handleInitTimeout() {
    this.state = 'disconnected'
    this.timedOut = true
    this.timedOutAt = Date.now()
    this.meeting.logEvent('secretary_participant_not_seen_60s', {
      secretary_observation_id: this.observationId,
      elapsed_ms: this.timedOutAt - this.connectStartedAt,
      deadline_ms: SECRETARY_INIT_TIMEOUT_MS,
    })
    this.startReconnectInterval()
    this.events.emit('timeout')
  }

  private stopInitTimeout() {
    if (this.initTimeout) {
      clearTimeout(this.initTimeout)
      this.initTimeout = null
    }
  }

  private startReconnectInterval() {
    if (this.meeting.isPreview || this.meeting.isDemo) return
    if (!this.meeting.currentUserIsGroupLeader) return
    if (this.reconnectInterval) return

    this.reconnectInterval = setInterval(() => {
      if (this.state === 'connected') {
        this.stopReconnectInterval()
        return
      }
      this.pumpSlideWithActiveSlideTouch()
    }, SECRETARY_RECONNECT_INTERVAL_MS)
  }

  private stopReconnectInterval() {
    if (this.reconnectInterval) {
      clearInterval(this.reconnectInterval)
      this.reconnectInterval = null
    }
  }

  private pumpSlideWithActiveSlideTouch() {
    if (!this.meeting.currentUserIsGroupLeader) return
    this.retryCount += 1
    this.meeting.logEvent('pump_slide_with_active_slide_touch', {
      slide_index: this.meeting.activeSlide,
      secretary_observation_id: this.observationId,
      retry_ordinal: this.retryCount,
    })
    roomStateUpdatePumpSlideWithActiveSlideChangedAt(this.meeting.firestore, {
      roomId: this.meeting.roomId,
    }).catch(() => {
      // Silently ignore — the interval will retry
    })
  }

  dispose() {
    // Idempotent: a double teardown (defensive cleanup, hot reload, error-path
    // double-dispose) must not emit observation_ended twice. Mark disposed
    // before logging so a throw in the event pipeline still latches it.
    if (this.disposed) return
    this.disposed = true
    if (this.initialized && !this.doesNotNeedSecretary) {
      const params: Record<string, unknown> = {
        secretary_observation_id: this.observationId,
        reason: 'tab_disposed',
      }
      if (this.state === 'connected' && this.connectedAt !== null) {
        params.connected_duration_ms = Date.now() - this.connectedAt
      }
      // Always land the run id on this terminal event when known. It guarantees
      // the observation can be joined to the backend run even if the id arrived
      // after the sighting event (via participantAttributesChanged), which no
      // seen/seen_after_loss event would have carried.
      if (this.observedRunId) {
        params.observed_transcription_run_id = this.observedRunId
      }
      this.meeting.logEvent('secretary_participant_observation_ended', params)
    }
    this.stopInitTimeout()
    this.stopReconnectInterval()
    this.disposers.dispose()
  }
}
